import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { MsalHelperService } from './msal-helper.service';
import { ContextResponse } from '../obj/Context';

@Injectable({
  providedIn: 'root'
})
export class ContextService {

  private context$ = new BehaviorSubject<ContextResponse | null>(null);
  private loadPromise: Promise<ContextResponse | null> | null = null;

  hasWorkspaceStorage$ = this.context$.pipe(
    map(c => c?.hasWorkspaceStorage === true)
  );

  hasTeamsLicense$ = this.context$.pipe(
    map(c => c?.hasTeamsEntitlement === true)
  );

  canUseGraphQL$ = combineLatest([this.hasWorkspaceStorage$, this.hasTeamsLicense$]).pipe(
    map(([storage, license]) => storage && license)
  );

  accounts$ = this.context$.pipe(
    map(c => c?.accounts ?? [])
  );

  clarityUser$ = this.context$.pipe(
    map(c => c?.clarityUser ?? null)
  );

  entitlement$ = this.context$.pipe(
    map(c => c?.entitlement ?? null)
  );

  constructor(private msalHelperService: MsalHelperService) {}

  private async getTokenWithRefreshFallback(scopes: string[]): Promise<{ accessToken: string } | null> {
    try {
      return await firstValueFrom(this.msalHelperService.getAccessTokenSilent$(scopes));
    } catch (firstError) {
      try {
        // Retry with forceRefresh when cache lookup fails with token_refresh_required.
        return await firstValueFrom(this.msalHelperService.getAccessTokenSilent$(scopes, true));
      } catch (refreshError) {
        this.msalHelperService.requestReauthPrompt(refreshError || firstError, scopes);
        return null;
      }
    }
  }

  async load(): Promise<ContextResponse | null> {
    const current = this.context$.value;
    if (current) {
      return current;
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.loadInternal().finally(() => {
      this.loadPromise = null;
    });

    return this.loadPromise;
  }

  /** Force a fresh fetch — bypasses the in-memory cache. Used after the user
   *  verifies their contact email so downstream subscribers see the new state. */
  async reload(): Promise<ContextResponse | null> {
    this.context$.next(null);
    if (this.loadPromise) {
      return this.loadPromise;
    }
    this.loadPromise = this.loadInternal().finally(() => {
      this.loadPromise = null;
    });
    return this.loadPromise;
  }

  private async loadInternal(): Promise<ContextResponse | null> {
    const result = await this.getTokenWithRefreshFallback([
      'https://service.flow.microsoft.com//Flows.Manage.All'
    ]);

    if (!result?.accessToken) {
      return null;
    }

    const env = /localhost|devmcp/.test(window.location.hostname) ? 'staging' : 'production';

    let res: Response;
    try {
      res = await fetch('/api/context', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${result.accessToken}`,
          'x-authorization': `Bearer ${result.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ environment: env, license: 'teams' }),
      });
    } catch {
      return null;
    }

    if (!res.ok) return null;

    const data: ContextResponse = await res.json();
    this.context$.next(data);
    return data;
  }
}
